/**
 * Role helpers for the admin panel.
 *
 * The logged-in user's role lives in localStorage under "userType" (written by
 * loginTabset). Role checks were previously written inline as
 * `localStorage.getItem("userType") === "merchant"` in every component; new
 * checks should go through the helpers here so the role list stays in one place.
 */

export const USER_TYPE = {
  CUSTOMER: "Customer",
  ADMIN: "admin",
  MERCHANT: "merchant",
  THIRD_PARTY: "thirdParty",
  WAITLIST_STAFF: "waitlist_staff",
  WAITER: "waiter",
  OPERATIONS: "operations",
};

// Roles pinned to a single merchant: they have no header merchant switcher, so
// their merchant always comes from localStorage rather than the redux selection.
const MERCHANT_SCOPED = [USER_TYPE.MERCHANT, USER_TYPE.OPERATIONS];

// Roles that may view a page but never mutate anything on it.
const READ_ONLY = [USER_TYPE.OPERATIONS];

export const getUserType = () => localStorage.getItem("userType");

const CAN_EXPORT_USERS = [USER_TYPE.ADMIN, USER_TYPE.MERCHANT];
// Mirrors ORDER_EXPORT_USER_TYPES on the API: operations may browse orders, not extract them.
const CAN_EXPORT_ORDERS = [USER_TYPE.ADMIN, USER_TYPE.MERCHANT];
// Mirrors PAYMENT_EXPORT_USER_TYPES on the API (payments report).
const CAN_EXPORT_PAYMENTS = [USER_TYPE.ADMIN, USER_TYPE.MERCHANT];

export const isMerchantScoped = (userType = getUserType()) => MERCHANT_SCOPED.includes(userType);
export const isReadOnly = (userType = getUserType()) => READ_ONLY.includes(userType);
export const isOperations = (userType = getUserType()) => userType === USER_TYPE.OPERATIONS;
export const isWaitlistStaff = (userType = getUserType()) => userType === USER_TYPE.WAITLIST_STAFF;

// Waitlist staff may also manage the unavailable products of their assigned branches' menus — but
// not the menus themselves. Mirrors MenuController's MENU_MANAGERS / BRANCH_MENU_STAFF on the API.
export const canManageMenus = (userType = getUserType()) => [USER_TYPE.ADMIN, USER_TYPE.MERCHANT].includes(userType);

// Branch ids a branch-scoped staff user was assigned at login.
export const getAssignedBranchIds = () => {
  try {
    const ids = JSON.parse(localStorage.getItem("assignedBranchIds") || "[]");
    return Array.isArray(ids) ? ids.map(Number) : [];
  } catch (e) {
    return [];
  }
};
// Adjusting a customer's wallet and loyalty balances on Customer Detail. Operations is read-only
// everywhere else; this mirrors the API's opsWrite on wallet credit/debit and loyalty adjust.
const CAN_ADJUST_CUSTOMER_BALANCES = [USER_TYPE.ADMIN, USER_TYPE.MERCHANT, USER_TYPE.OPERATIONS];
export const canAdjustCustomerBalances = (userType = getUserType()) =>
  CAN_ADJUST_CUSTOMER_BALANCES.includes(userType);

export const canExportUsers = (userType = getUserType()) => CAN_EXPORT_USERS.includes(userType);
export const canExportOrders = (userType = getUserType()) => CAN_EXPORT_ORDERS.includes(userType);
export const canExportPayments = (userType = getUserType()) => CAN_EXPORT_PAYMENTS.includes(userType);

/**
 * The only paths an operations user may visit — everything else bounces to
 * OPERATIONS_HOME (enforced in FullLayout).
 *
 * Kept lowercase because route paths in this app are inconsistently cased
 * (`/Orders/Orders` vs `/orders/arrivals`) and react-router matches
 * case-insensitively, while String.startsWith does not.
 */
export const OPERATIONS_ALLOWED_PATHS = [
  "/offers/coupons",
  "/orders/orders",
  "/reporting/steppickup-analytics",
  "/reporting/customer-loyalty",
  // Row drill-down from Customer & Loyalty; no menu leaf, so it only shows up via that page.
  "/reporting/customer-detail",
  "/users/list-user",
  "/products/hidden-modifiers",
  // Read-only: preview and performance; the save controls are disabled for this role.
  "/recommendations",
  // Read-only: list, view and preview; every write control is hidden for this role.
  "/pages",
  // Read-only: browse and copy links; upload, rename and delete are hidden for this role.
  "/media",
];

export const OPERATIONS_HOME = "/Orders/Orders";

// Landing page per role. Roles with no dashboard access need somewhere else to
// go, both after login and when they hit "/".
const LANDING_BY_USER_TYPE = {
  [USER_TYPE.WAITLIST_STAFF]: "/table-management/waitlist",
  [USER_TYPE.OPERATIONS]: OPERATIONS_HOME,
};

export const landingPathFor = (userType = getUserType()) =>
  LANDING_BY_USER_TYPE[userType] || "/dashboard";

export const isOperationsAllowedPath = (pathname = "") =>
  OPERATIONS_ALLOWED_PATHS.some((p) => pathname.toLowerCase().startsWith(p));

// Labels for the userType column / filters in the user list.
export const USER_TYPE_LABELS = {
  customer: "Customer",
  merchant: "Merchant",
  admin: "Admin",
  thirdparty: "Third Party",
  waiter: "Waiter",
  waitlist_staff: "Waitlist Staff",
  operations: "Operations",
};
